import React from 'react';
import { motion } from 'framer-motion';
import { CloudIcon, CogIcon, ChartPieIcon } from '@heroicons/react/24/outline';

const RiskModels: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-900 to-primary-800 text-white pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl pt-8">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl font-bold mb-6"
            >
              Risk Models
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl mb-8"
            >
              Real-time risk modeling using validated data feeds, satellite imagery, and machine learning models.
            </motion.p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="p-6 rounded-xl border border-secondary-200"
          >
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
              <CloudIcon className="w-6 h-6 text-primary-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Data Sources</h3>
            <ul className="list-disc list-inside text-secondary-600 mt-2 space-y-1">
              <li>Satellite imagery analysis</li>
              <li>Weather data integration</li>
              <li>Historical wildfire patterns</li>
              <li>Ground sensor networks</li>
            </ul>
          </motion.div>

          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="p-6 rounded-xl border border-secondary-200"
          >
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
              <CogIcon className="w-6 h-6 text-primary-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">AI Processing</h3>
            <ul className="list-disc list-inside text-secondary-600 mt-2 space-y-1">
              <li>LLM-powered risk assessment</li>
              <li>Real-time data processing</li>
              <li>Automated pattern detection</li>
              <li>Predictive modeling</li>
            </ul>
          </motion.div>

          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="p-6 rounded-xl border border-secondary-200"
          >
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
              <ChartPieIcon className="w-6 h-6 text-primary-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Risk Analytics</h3>
            <ul className="list-disc list-inside text-secondary-600 mt-2 space-y-1">
              <li>Dynamic risk scoring</li>
              <li>Premium calculation</li>
              <li>Coverage optimization</li>
              <li>Portfolio analysis</li>
            </ul>
          </motion.div>
        </div>

        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold mb-6">Model Architecture</h2>
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-2">1. Data Collection</h3>
              <p className="text-secondary-600">
                Continuous ingestion of real-time data from multiple sources, including satellite feeds,
                weather stations, and IoT sensors. All data is validated through our decentralized network.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">2. AI Processing</h3>
              <p className="text-secondary-600">
                Advanced machine learning models analyze the data in real-time, identifying patterns
                and potential risk factors. Models are continuously trained on new data.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">3. Risk Assessment</h3>
              <p className="text-secondary-600">
                Comprehensive risk profiles are generated for each area, considering multiple factors
                and historical data. Risk scores are updated dynamically as conditions change.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">4. Validation & Consensus</h3>
              <p className="text-secondary-600">
                All risk assessments are validated by our network of validators through EigenLayer's
                AVS framework, ensuring accuracy and reliability of the results.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskModels; 